import React, { useState } from "react";
import { t } from "ttag";

import type { SimpleLinkBlock } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { Clickable } from "../Clickables";
import { RichText } from "../RichText";
import { Svg } from "../Svg";

import styles from "./GlassCard.module.scss";

export interface DataRow {
    label: string;
    description: string;
}

interface GlassCardProps {
    heading: string;
    body: string;
    headingTag?: "h2" | "h3" | "h4";
    link?: SimpleLinkBlock | null;
    dataEyebrow?: string | null;
    dataRows?: readonly DataRow[];
    collapsible?: boolean;
    className?: string;
}

export const GlassCard = ({
    headingTag: HeadingTag = "h2",
    ...props
}: Readonly<GlassCardProps>) => {
    const [userExpanded, setUserExpanded] = useState(false);
    const isExpanded = !props.collapsible || userExpanded;
    const hasDataRows = props.dataRows && props.dataRows.length > 0;

    return (
        <div
            className={concatClassNames([
                styles.card,
                !isExpanded ? styles.collapsed : undefined,
                props.className,
            ])}
        >
            <div className={styles.header}>
                <HeadingTag className={styles.heading}>
                    {props.heading}
                </HeadingTag>
                {props.collapsible && (
                    <Clickable
                        className={styles.toggle}
                        onClick={() => setUserExpanded((prev) => !prev)}
                        aria-expanded={isExpanded}
                        aria-label={
                            isExpanded ? t`Collapse details` : t`Expand details`
                        }
                    >
                        <Svg
                            name={isExpanded ? "indeterminate-dash" : "plus"}
                            aria-hidden="true"
                        />
                    </Clickable>
                )}
            </div>

            <div className={styles.content}>
                <RichText
                    className={styles.body}
                    trimTerminalMargins="both"
                    value={props.body}
                />

                {props.link?.url && (
                    <Clickable simpleLink={props.link} className={styles.link}>
                        {props.link.display_name}
                    </Clickable>
                )}

                {hasDataRows && (
                    <div className={styles.dataSection}>
                        {props.dataEyebrow && (
                            <p className={styles.dataEyebrow}>
                                {props.dataEyebrow}
                            </p>
                        )}
                        <div className={styles.dataRows}>
                            {props.dataRows?.map((row, index) => (
                                <div key={index} className={styles.dataRow}>
                                    <span className={styles.dataLabel}>
                                        {row.label}
                                    </span>
                                    <span className={styles.dataDescription}>
                                        {row.description}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
